import React, { Component, ErrorInfo, ReactNode } from 'react'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  }

  public static getDerivedStateFromError(error: Error): State {
    // Check if this is a ResizeObserver error - if so, don't set hasError
    const isResizeObserverError =
      error.message?.includes('ResizeObserver loop completed with undelivered notifications') ||
      error.message?.includes('ResizeObserver loop limit exceeded') ||
      error.message?.includes('ResizeObserver loop')

    if (isResizeObserverError) {
      // Silently ignore ResizeObserver errors
      return { hasError: false, error: null }
    }

    // For other errors, update state so the next render will show the fallback UI
    return { hasError: true, error }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Check if this is a ResizeObserver error - if so, ignore it
    const isResizeObserverError =
      error.message?.includes('ResizeObserver loop completed with undelivered notifications') ||
      error.message?.includes('ResizeObserver loop limit exceeded') ||
      error.message?.includes('ResizeObserver loop')

    if (isResizeObserverError) {
      // Silently ignore ResizeObserver errors
      return
    }

    // Log other errors
    console.error('Uncaught error:', error, errorInfo)
  }

  public render() {
    if (this.state.hasError) {
      // Render custom fallback UI for real errors
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Something went wrong</h1>
            <p className="text-gray-600 mb-4">
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null })
                window.location.reload()
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Reload Page
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

